import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Buildings, CheckSquare, FileText, DownloadSimple, Copy, PencilSimple, PaperPlaneTilt, Trash, ChartLineUp, ChatCircleText } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { StatusBadge, fmtDate, EmptyState } from "./ui";
import { CLIENT_TYPES, STATUSES, ServiceSelect } from "./Clients";
import { MessageThread } from "@/components/MessageThread";
import { AccountsVault } from "@/components/AccountsVault";
import { ClientBilling } from "@/components/ClientBilling";
import { SpecialProfile } from "@/components/SpecialProfile";
import { AIAssistant } from "@/components/AIAssistant";
import { Goals } from "@/components/Goals";

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm mt-0.5">{value || "—"}</div>
  </div>
);

const maskSsn = (s) => {
  if (!s) return null;
  const digits = String(s).replace(/\D/g, "");
  return digits ? `•••-••-${digits.slice(-4)}` : null;
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [scores, setScores] = useState([]);
  const [scoreEditId, setScoreEditId] = useState(null);
  const [scoreForm, setScoreForm] = useState({ score: "", bureau: "CreditHero", date: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([api.get(`/cc/clients/${id}`), api.get(`/cc/clients/${id}/scores`)]);
      setData(r.data); setScores(s.data);
    } catch { setData(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="text-muted-foreground">Loading…</div>;
  if (data === false) return <div className="text-red-300">Client not found.</div>;

  const { client, companies, tasks, documents } = data;
  const svcs = (client.services && client.services.length) ? client.services : (client.service_package ? [client.service_package] : []);
  const hasSpecial = svcs.includes("Special Profile");

  const viewDoc = async (docId) => {
    try {
      const res = await api.get(`/cc/documents/${docId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const deleteClient = async () => {
    if (!window.confirm(`Permanently delete ${client.full_name || client.business_name}? This cannot be undone.`)) return;
    try { await api.delete(`/cc/clients/${id}?archive=false`); toast.success("Client deleted"); navigate("/command/clients"); }
    catch { toast.error("Failed to delete client"); }
  };
  const addScore = async () => {
    if (!scoreForm.score) return toast.error("Enter a score");
    try {
      const payload = { score: parseInt(scoreForm.score, 10), bureau: scoreForm.bureau, date: scoreForm.date || undefined, notes: scoreForm.notes };
      if (scoreEditId) await api.put(`/cc/scores/${scoreEditId}`, payload);
      else await api.post(`/cc/clients/${id}/scores`, payload);
      setScoreForm({ score: "", bureau: "CreditHero", date: "", notes: "" });
      setScoreEditId(null);
      const s = await api.get(`/cc/clients/${id}/scores`); setScores(s.data);
      toast.success(scoreEditId ? "Score updated" : "Score added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const editScore = (s) => { setScoreEditId(s.id); setScoreForm({ score: String(s.score), bureau: s.bureau || "CreditHero", date: s.date || "", notes: s.notes || "" }); };
  const delScore = async (sid) => {
    try { await api.delete(`/cc/scores/${sid}`); setScores((r) => r.filter((x) => x.id !== sid)); }
    catch { toast.error("Failed"); }
  };

  const copyForm = (kind) => {
    const link = `${window.location.origin}/forms/${kind}${client.email ? `?email=${encodeURIComponent(client.email)}` : ""}`;
    if (navigator.clipboard) navigator.clipboard.writeText(link);
    toast.success("Form link copied to clipboard");
  };

  const openEdit = () => {
    setForm({
      client_type: client.client_type || "individual",
      full_name: client.full_name || "",
      business_name: client.business_name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      city: client.city || "",
      state: client.state || "",
      zip_code: client.zip_code || "",
      ssn: client.ssn || "",
      dob: client.dob || "",
      status: client.status || "Active",
      service_package: client.service_package || "",
      services: (client.services && client.services.length) ? client.services : (client.service_package ? [client.service_package] : []),
      emergency_contact: client.emergency_contact || "",
      notes: client.notes || "",
      portal_access: !!client.portal_access,
    });
    setEditOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/cc/clients/${id}`, form);
      setData((d) => ({ ...d, client: r.data }));
      toast.success("Client updated");
      setEditOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update client");
    } finally { setSaving(false); }
  };

  const togglePortal = async (v) => {
    try {
      const r = await api.patch(`/cc/clients/${id}/portal`, { portal_access: v });
      setData((d) => ({ ...d, client: r.data }));
      toast.success(v ? "Portal access enabled" : "Portal access disabled");
    } catch { toast.error("Failed to update portal access"); }
  };

  const invite = async () => {
    if (!client.email) return toast.error("Add an email to this client first, then invite them.");
    setInviting(true);
    try {
      const r = await api.post(`/cc/clients/${id}/invite`);
      setData((d) => ({ ...d, client: { ...d.client, portal_access: true, invited_at: new Date().toISOString() } }));
      toast.success(r.data?.emailed ? `Invite sent to ${r.data.email}` : "Invite created (email pending)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send invite");
    } finally { setInviting(false); }
  };

  return (
    <div className="space-y-8" data-testid="client-detail-page">
      <button onClick={() => navigate("/command/clients")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-clients">
        <ArrowLeft size={16} /> Back to clients
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">{client.full_name || client.business_name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <StatusBadge status={client.status} />
            <span className="text-xs text-muted-foreground capitalize">{client.client_type} client · added {fmtDate(client.date_added)}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={openEdit} data-testid="edit-client-button" className="gap-2 border-white/15"><PencilSimple size={16} /> Edit</Button>
          <Button variant="outline" size="sm" onClick={invite} disabled={inviting} data-testid="invite-client-button" className="gap-2 border-primary/40 text-primary hover:bg-primary/10">
            <PaperPlaneTilt size={16} /> {inviting ? "Sending…" : client.invited_at ? "Resend invite" : "Invite to portal"}
          </Button>
          <Button variant="outline" size="sm" onClick={deleteClient} data-testid="delete-client-button" className="gap-2 border-red-500/30 text-red-300 hover:bg-red-500/10"><Trash size={16} /> Delete</Button>
          <div className="flex items-center gap-3 rounded-lg border border-white/10 px-4 py-3">
            <div>
              <div className="text-sm font-medium">Client portal</div>
              <div className="text-xs text-muted-foreground">{client.portal_access ? "Enabled" : "Disabled"}</div>
            </div>
            <Switch checked={!!client.portal_access} onCheckedChange={togglePortal} data-testid="detail-portal-switch" />
          </div>
        </div>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit Client</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Update this client's profile information.</DialogDescription></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div>
                <Label>Client type</Label>
                <Select value={form.client_type} onValueChange={(v) => set("client_type", v)}>
                  <SelectTrigger data-testid="edit-client-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full name</Label><Input data-testid="edit-client-fullname-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></div>
                <div><Label>Business name</Label><Input data-testid="edit-client-business-input" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} /></div>
                <div><Label>Email</Label><Input data-testid="edit-client-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
                <div><Label>Phone</Label><Input data-testid="edit-client-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                <div><Label>Date of birth</Label><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Street address</Label><Input data-testid="edit-client-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>City</Label><Input data-testid="edit-client-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} /></div>
                <div><Label>State</Label><Input data-testid="edit-client-state-input" value={form.state} onChange={(e) => set("state", e.target.value)} /></div>
                <div><Label>Zip</Label><Input data-testid="edit-client-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} /></div>
              </div>
              <div><Label>Social Security Number</Label><Input data-testid="edit-client-ssn-input" value={form.ssn} onChange={(e) => set("ssn", e.target.value)} placeholder="___-__-____" /></div>
              <div>
                <Label>Services</Label>
                <div className="mt-1"><ServiceSelect value={form.services} onChange={(v) => set("services", v)} /></div>
              </div>
              <div><Label>Emergency contact</Label><Input data-testid="edit-client-emergency-input" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button data-testid="save-edit-client-button" disabled={saving} onClick={saveEdit} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Info label="Full name" value={client.full_name} />
        <Info label="Business name" value={client.business_name} />
        <Info label="Email" value={client.email} />
        <Info label="Phone" value={client.phone} />
        <Info label="Address" value={client.address} />
        <Info label="City / State / Zip" value={[client.city, client.state, client.zip_code].filter(Boolean).join(", ")} />
        <Info label="SSN" value={maskSsn(client.ssn)} />
        <Info label="Date of birth" value={client.dob && fmtDate(client.dob)} />
        <div className="sm:col-span-2 lg:col-span-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Services</div>
          {svcs.length === 0 ? <div className="text-sm mt-0.5">—</div> : (
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="client-services-tags">
              {svcs.map((s) => <span key={s} className="rounded-full border border-primary/40 bg-primary/10 text-primary px-3 py-1 text-xs">{s}</span>)}
            </div>
          )}
        </div>
        <Info label="Emergency contact" value={client.emergency_contact} />
      </div>

      <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6" data-testid="forms-share">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Send intake forms</div>
        <p className="text-sm text-muted-foreground mb-4">Copy a signing link for this client. Submissions are auto-attached to this profile.</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="sm" onClick={() => copyForm("personal-lpoa")} data-testid="copy-personal-form" className="gap-2 border-white/15"><Copy size={16} /> Personal Authorization & LPOA</Button>
          <Button variant="outline" size="sm" onClick={() => copyForm("business-lpoa")} data-testid="copy-business-form" className="gap-2 border-white/15"><Copy size={16} /> Business Authorization & LPOA</Button>
        </div>
      </div>

      {client.credit_hero && (
        <div className="rounded-xl border border-primary/25 bg-primary/5 p-6" data-testid="credit-hero-info">
          <div className="text-xs uppercase tracking-wider text-primary mb-3">Credit Hero login</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Info label="Login" value={client.credit_hero.login} />
            <Info label="Password" value={client.credit_hero.password} />
          </div>
          <p className="text-xs text-muted-foreground mt-3">Submitted {client.credit_hero.submitted_at && fmtDate(client.credit_hero.submitted_at)}. The uploaded credit report is in Documents below.</p>
        </div>
      )}

      {client.notes && (
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Internal notes (admin only)</div>
          <p className="text-sm whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}

      <section data-testid="client-ai-section">
        <AIAssistant clientId={id} />
      </section>

      <section data-testid="client-billing-section">
        <ClientBilling clientId={id} />
      </section>

      {hasSpecial && (
        <section>
          <SpecialProfile clientId={id} />
        </section>
      )}

      <section data-testid="client-accounts-section">
        <AccountsVault clientId={id} />
      </section>

      <section data-testid="credit-scores-section">
        <div className="flex items-center gap-2 mb-4"><ChartLineUp size={20} className="text-primary" /><h2 className="text-lg font-medium">Credit Score History ({scores.length})</h2></div>
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 p-5">
          {scores.length > 0 && (
            <div className="flex items-end gap-6 mb-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Latest</div>
                <div className="font-display text-4xl tracking-tighter text-primary">{scores[scores.length - 1].score}</div>
                <div className="text-xs text-muted-foreground">{scores[scores.length - 1].bureau} · {scores[scores.length - 1].date}</div>
              </div>
              <div className="flex items-end gap-1 h-16 flex-1">
                {scores.slice(-12).map((s) => (
                  <div key={s.id} className="flex-1 bg-primary/30 rounded-t" style={{ height: `${Math.max(8, Math.min(100, ((s.score - 300) / 550) * 100))}%` }} title={`${s.score} (${s.date})`} />
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
            <div><Label className="text-xs">Score</Label><Input type="number" data-testid="score-input" value={scoreForm.score} onChange={(e) => setScoreForm((f) => ({ ...f, score: e.target.value }))} /></div>
            <div>
              <Label className="text-xs">Bureau</Label>
              <Select value={scoreForm.bureau} onValueChange={(v) => setScoreForm((f) => ({ ...f, bureau: v }))}>
                <SelectTrigger data-testid="score-bureau-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["CreditHero", "Equifax", "Experian", "TransUnion"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Month</Label><Input type="month" data-testid="score-date-input" value={scoreForm.date} onChange={(e) => setScoreForm((f) => ({ ...f, date: e.target.value }))} /></div>
            <div className="sm:col-span-1"><Label className="text-xs">Note</Label><Input value={scoreForm.notes} onChange={(e) => setScoreForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            <Button onClick={addScore} data-testid="add-score-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{scoreEditId ? "Save" : "Add"}</Button>
          </div>
          {scores.length > 0 && (
            <div className="mt-4 divide-y divide-white/5" data-testid="scores-list">
              {[...scores].reverse().map((s) => (
                <div key={s.id} className="flex items-center justify-between py-2 text-sm">
                  <span>{s.score} <span className="text-muted-foreground">· {s.bureau} · {s.date}{s.notes ? ` · ${s.notes}` : ""}</span></span>
                  <span className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" onClick={() => editScore(s)} data-testid={`edit-score-${s.id}`} className="h-7 w-7"><PencilSimple size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delScore(s.id)} className="h-7 w-7 text-red-300 hover:text-red-200"><Trash size={14} /></Button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4"><Buildings size={20} className="text-primary" /><h2 className="text-lg font-medium">Companies ({companies.length})</h2></div>
        {companies.length === 0 ? <EmptyState title="No companies linked" hint="Add a company from the Companies section." /> : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="detail-companies">
            {companies.map((c) => (
              <div key={c.id} className="rounded-lg border border-white/10 bg-zinc-900/50 p-4 flex items-center justify-between">
                <div><div className="font-medium">{c.legal_name}</div><div className="text-xs text-muted-foreground">{[c.entity_type, c.state].filter(Boolean).join(" · ")}</div></div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4"><CheckSquare size={20} className="text-primary" /><h2 className="text-lg font-medium">Tasks ({tasks.length})</h2></div>
        {tasks.length === 0 ? <EmptyState title="No tasks" /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="detail-tasks">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between px-4 py-3">
                <div><div className="text-sm font-medium">{t.name}</div><div className="text-xs text-muted-foreground">due {fmtDate(t.due_date)}</div></div>
                <StatusBadge status={t.status} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-4"><FileText size={20} className="text-primary" /><h2 className="text-lg font-medium">Documents ({documents.length})</h2></div>
        {documents.length === 0 ? <EmptyState title="No documents" /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="detail-documents">
            {documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between px-4 py-3">
                <div><div className="text-sm font-medium">{d.name}</div><div className="text-xs text-muted-foreground">{d.category} · {d.client_visible ? "Client visible" : "Private"}</div></div>
                <Button size="icon" variant="ghost" onClick={() => viewDoc(d.id)} data-testid={`detail-view-doc-${d.id}`}><DownloadSimple size={18} /></Button>
              </div>
            ))}
          </div>
        )}
      </section>
      <section data-testid="client-goals-section">
        <Goals clientId={id} />
      </section>

      <section data-testid="client-messages">
        <div className="flex items-center gap-2 mb-4"><ChatCircleText size={20} className="text-primary" /><h2 className="text-lg font-medium">Messages</h2></div>
        <MessageThread listUrl={`/cc/clients/${id}/messages`} postUrl={`/cc/clients/${id}/messages`} self="admin" />
      </section>
    </div>
  );
}
